/**
 * Create-report panel for VSC reports.
 */
import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, CalendarDays } from 'lucide-react';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { DatePicker } from '@/components/forms/DatePicker';
import { useEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';
import { createVscReports } from '@/services/vsc-reports.service';
import type { CreateVscReportFormState } from '@/types/vsc-report.types';
import { VSC_REPORT_FORM_QUERY_KEYS } from './vsc-report-configs';
import {
  applyVscReportFormField,
  buildCreateVscReportDto,
  emptyCreateVscReportForm,
  isCreateVscReportFormValid,
  resolveCreateVscReportOutcome,
} from './vsc-report-form-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';

/** Props for {@link CreateVscReportView}. */
interface CreateVscReportViewProps {
  onClose: () => void;
  onSuccess?: () => void;
  className?: string;
}

/** Create-report panel for VSC reports. */
export const CreateVscReportView: React.FC<Readonly<CreateVscReportViewProps>> = ({
  onClose,
  onSuccess,
  className,
}) => {
  const queryClient = useQueryClient();
  const {
    options: establishmentOptions,
    isLoading: establishmentSearchLoading,
    onSearch: onEstablishmentSearch,
  } = useEstablishmentsAutocomplete({ operationalOnly: true });

  const [formData, setFormData] =
    React.useState<CreateVscReportFormState>(emptyCreateVscReportForm);
  const isValid = isCreateVscReportFormValid(formData);

  /** Patch one create-form field (establishment or report date). */
  const handleFieldChange = (field: keyof CreateVscReportFormState, value: string) => {
    setFormData((current) => applyVscReportFormField(current, field, value));
  };

  const createMutation = useMutation({
    mutationFn: () => createVscReports(buildCreateVscReportDto(formData)),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: VSC_REPORT_FORM_QUERY_KEYS.list,
          refetchType: 'all',
        }),
        queryClient.invalidateQueries({
          queryKey: VSC_REPORT_FORM_QUERY_KEYS.onePrefix,
          refetchType: 'all',
        }),
      ]);
      const outcome = resolveCreateVscReportOutcome(result);
      if (outcome.kind === 'alreadyExists') {
        toast.info(getErrorMessage(outcome.message));
        return;
      }
      toast.success(outcome.message);
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  return (
    <CollapseReplaceForm
      formType="create"
      title="Créer un rapport VSC"
      subtitle="Renseignez les informations du rapport VSC"
      onClose={onClose}
      onSubmit={() => {
        if (isValid) createMutation.mutate();
      }}
      submitLabel="Générer"
      isSubmitting={createMutation.isPending}
      isValid={isValid}
      className={className}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <Building2 className="w-4 h-4" />
          Établissement
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Autocomplete
            label="Établissement"
            placeholder="Rechercher un établissement..."
            options={establishmentOptions}
            value={formData.establishmentId}
            onChange={(value) => handleFieldChange('establishmentId', value)}
            onSearch={onEstablishmentSearch}
            isLoading={establishmentSearchLoading}
            required
            helperText="Un rapport sera créé pour chaque VSC rattaché à cet établissement."
          />
        </div>
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <CalendarDays className="w-4 h-4" />
          Date du rapport
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DatePicker
            label="Date"
            value={formData.reportDate}
            onChange={(value) => handleFieldChange('reportDate', value)}
            required
            placement="top"
          />
        </div>
      </div>
    </CollapseReplaceForm>
  );
};
